import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { influencers, InfluencerTypes } from 'api';
import { DealsChartOption, InfluencerChartOption } from 'types/influencers';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from 'helpers/formatters';
import { getChartOptions } from 'helpers/chartHelpers';
import { Stack } from '@mui/material';
import ComparisonChart from '../ComparisonChart';
import BudgetRevenueChart from './BudgetRevenueChart';

interface Props {
  data: InfluencerTypes.ItemExtended;
}

const DealsChart = (props: Props): JSX.Element | null => {
  const { data: influencerData } = props;

  const influencerId = useMemo(
    () => influencerData?._id?.$oid,
    [influencerData],
  );

  const [chartDataType, setChartDataType] =
    useState<DealsChartOption['type']>('roi');

  const { data, isLoading } = useQuery(
    [influencers.endpoints.getDealsChartById(influencerId), chartDataType],
    () => influencers.getDealsChartById(influencerId, chartDataType),
  );

  const chartData = useMemo(() => {
    if (data) {
      return getChartOptions({
        key: chartDataType,
        chartData: data,
        avgData: 'global_avg_details' in data ? data.global_avg_details : {},
      });
    }

    return [];
  }, [chartDataType, data]);

  const tabs = useMemo(
    () =>
      [
        {
          type: 'roi',
          label: 'Return of investment (ROI)',
          value: formatNumber(influencerData.roi),
        },
        {
          type: 'budget',
          label: 'Average budget',
          value: formatCurrency(influencerData.avg_budget),
        },
        {
          type: 'revenue',
          label: 'Revenue per deal',
          value: formatCurrency(influencerData.avg_revenue),
        },
        {
          type: 'cpo',
          label: 'Cost per order (CPO)',
          value: formatCurrency(influencerData.cpo),
        },
        {
          type: 'cpm',
          label: 'Cost per 1000 views (CPM)',
          value: formatCurrency(influencerData.avg_cpm),
        },
        {
          type: 'rpm',
          label: 'RPM',
          value: formatCurrency(influencerData.rpm),
        },
        {
          type: 'cr',
          label: 'Conversion rate',
          value: formatPercent(influencerData.avg_cr),
        },
      ] as DealsChartOption[],
    [influencerData],
  );

  return (
    <Stack rowGap={2}>
      <BudgetRevenueChart data={influencerData} />
      <ComparisonChart
        isLoading={isLoading}
        chartData={chartData}
        tabs={tabs}
        chartDataType={chartDataType}
        setChartDataType={
          setChartDataType as React.Dispatch<
            React.SetStateAction<InfluencerChartOption['type']>
          >
        }
      />
    </Stack>
  );
};

export default DealsChart;
